"use client"
import { useAppStoreContext } from "@/stores/appStore"
import { HeaderNavBar } from "@/components/generic/HeaderNavBar/HeaderNavBar"
import dynamic from "next/dynamic"
import { Skeleton, Box } from "@mantine/core"

import { useMemo, type ReactNode } from "react"
import { syncObservable } from "@legendapp/state/sync"
import { dynamoStore$ } from "@/stores/dynamo.v2.store"
import { observablePersistIndexedDB } from "@legendapp/state/persist-plugins/indexeddb"
import { useObserve } from "@legendapp/state/react"
import { syncStore$ } from "@/stores/sync.v2.store"
import classes from "./default.client.layout.module.css"
import clsx from "clsx"
import { WebphonePlaceholder } from "@/components/generic/WebphonePlaceholder/WebphonePlaceholder"
import { chatStore$ } from "@/lib/ContactsMap"
import { usePathname } from "next/navigation"
import { SkeletonSideNavBar } from "@/components/generic/SideNavBar/SkeletonSideNavBar"

const SideNavBar = dynamic(() => import("@/components/generic/SideNavBar/SideNavBar").then((mod) => mod.SideNavBar), {
  ssr: false,
  loading: () => <SkeletonSideNavBar />,
})

const FULLSCREEN_PATHS = ["/login", "/recover-password", "/sequences", "/sign-up", "/subscribe", "/subscribe/team", "/legacy-system"]

export function ChildrenLayout({
  children,
  classNames,
  showSequenceFeature,
}: {
  children: ReactNode
  classNames?: {
    root?: string
  }
  showSequenceFeature?: boolean | null
}) {
  const pathname = usePathname()
  const webphoneExpanded = useAppStoreContext((s) => s.webphoneExpanded)

  const FULLSCREEN_EXACT_EXCLUDE_PATHS = useMemo(() => {
    return ["/sequences/library"]
  }, [])

  const isFullscreen = useMemo(() => {
    let _isFullscreen = false

    if (FULLSCREEN_EXACT_EXCLUDE_PATHS.includes(pathname)) {
      return false
    }

    for (const path of FULLSCREEN_PATHS) {
      if (pathname.startsWith(path)) {
        _isFullscreen = true
        break
      }
    }

    return _isFullscreen
  }, [FULLSCREEN_EXACT_EXCLUDE_PATHS, pathname])

  const containerClasses = useMemo(() => {
    if (isFullscreen) return classes.fullscreen
    if (webphoneExpanded) return clsx(classes.noFullScreen, classes.noFullScreenExpanded)
    return clsx(classes.noFullScreen, classes.noFullScreenCollapsed)
  }, [isFullscreen, webphoneExpanded])

  useObserve(() => {
    const accountID = syncStore$.userStatus.accountId.get()
    if (!accountID) return

    syncObservable(dynamoStore$, {
      persist: {
        name: "dynamoStore_v3", // This is the IndexedDB table name where we will store this "store"
        plugin: observablePersistIndexedDB({
          version: 3,
          databaseName: `Ringio-${encodeURIComponent(process.env.NEXT_PUBLIC_FRONT_URL ?? "unknown")}-${accountID}`,
          tableNames: ["chatStore_v3", "dynamoStore_v3"],
        }),
        transform: {
          load: (data) => {
            delete data?.liveUserContactInteractions

            delete data?._recentUserInteractionLastKey
            delete data?.isFetchingRecentUserInteractions

            delete data?._recentAccountInteractionLastKey
            delete data?.isFetchingRecentAccountInteractions

            delete data?._recentUserContactsLastKey
            delete data?.isFetchingRecentUserContacts

            delete data?._recentAccountContactsLastKey
            delete data?.isFetchingRecentAccountContacts

            delete data?._recentContactInteractionsLastKey
            delete data?.isFetchingRecentContactInteractions

            return data
          },
          save: (data) => {
            // In development, even if we don't use it, I want to save them, to visualize them
            if (process.env.NODE_ENV !== "development") {
              delete data?.liveUserContactInteractions

              delete data?._recentUserInteractionLastKey
              delete data?.isFetchingRecentUserInteractions

              delete data?._recentAccountInteractionLastKey
              delete data?.isFetchingRecentAccountInteractions

              delete data?._recentUserContactsLastKey
              delete data?.isFetchingRecentUserContacts

              delete data?._recentAccountContactsLastKey
              delete data?.isFetchingRecentAccountContacts

              delete data?._recentContactInteractionsLastKey
              delete data?.isFetchingRecentContactInteractions
            }
            return data
          },
        },
      },
    })

    syncObservable(chatStore$, {
      persist: {
        name: "chatStore_v4", // This is the IndexedDB table name where we will store this "store"
        // We use indexedDB so we don't have to worry about the size of the data we store
        plugin: observablePersistIndexedDB({
          version: 4,
          databaseName: `Ringio-${encodeURIComponent(process.env.NEXT_PUBLIC_FRONT_URL ?? "unknown")}-${accountID}`,
          tableNames: ["chatStore_v4", "dynamoStore_v4"],
        }),
        transform: {
          load: (data) => {
            // I think we don't need to save this
            // delete data?.interactions
            //
            delete data?.unreadChats
            delete data?.chats
            delete data?.sortedInteractions
            delete data?.searchChats
            delete data?.liveCalls
            delete data?._internal?.chatsMap
            delete data?._internal?.searchChatsMap
            delete data?._internal?.liveCallsMap
            delete data?._internal?.sortedInteractionsMap
            delete data?.chatLastInteraction

            return data
          },
          save: (data) => {
            // In development, even if we don't use it, I want to save them, to visualize them
            if (process.env.NODE_ENV !== "development") {
              // I think we don't need to save this
              // delete data?.interactions
              //
              delete data?.unreadChats
              delete data?.chats
              delete data?.sortedInteractions
              delete data?.searchChats
              delete data?.liveCalls
              delete data?._internal?.chatsMap
              delete data?._internal?.searchChatsMap
              delete data?._internal?.liveCallsMap
              delete data?._internal?.sortedInteractionsMap
              delete data?.chatLastInteraction
            }
            return data
          },
        },
      },
    })
  })

  return (
    <>
      {isFullscreen ? null : (
        <Box className={classes.headerNavBarContainer}>
          <HeaderNavBar />
        </Box>
      )}
      <Box className={clsx(containerClasses, classNames?.root)}>
        {isFullscreen ? null : (
          <Box className={classes.sideNavBarContainer}>
            <SideNavBar showSequenceFeature={showSequenceFeature} />
          </Box>
        )}
        <Box className={clsx(classes.appContainer, isFullscreen ? classes.isFullscreenAppContainer : classes.isNotFullscreenAppContainer)}>
          <Box className={classes.childrenContainer}>{children}</Box>
        </Box>
        <WebphonePlaceholder webphoneCollapsed={!webphoneExpanded} />
      </Box>
    </>
  )
}
